import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header.jsx";
import { useGetAllQuery, useGetRolesQuery, useSetRoleMutation } from "../../services/admin.service.js";

// Role management: the only place roles (including "chief") are granted or revoked.
const BoardAdmin = () => {
  const theme = useTheme();
  const { user: currentUser } = useSelector((state) => state.auth);
  const { data, isLoading } = useGetAllQuery();
  const { data: roles } = useGetRolesQuery();
  const [setRole, { isLoading: saving }] = useSetRoleMutation();
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState(null);

  const handleConfirm = () => {
    const { user, role, grant } = pending;
    setPending(null);
    setRole({ userId: user._id, role, grant })
      .unwrap()
      .then(() =>
        setNotice({
          severity: "success",
          text: grant ? `${user.username} is now ${role}.` : `Removed ${role} from ${user.username}.`,
        })
      )
      .catch((err) => setNotice({ severity: "error", text: err?.data?.message || "Failed to change role." }));
  };

  const roleColumns = (roles || []).map((role) => ({
    field: `role_${role}`,
    headerName: role,
    width: 110,
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    valueGetter: ({ row }) => row.roles.some((r) => r.name === role),
    renderCell: ({ row, value }) => {
      const ownAdmin = role === "admin" && row._id === currentUser?.id;
      const checkbox = (
        <Checkbox
          checked={value}
          disabled={ownAdmin || saving}
          color="secondary"
          onChange={() => setPending({ user: row, role, grant: !value })}
        />
      );
      return ownAdmin ? (
        <Tooltip title="You cannot remove your own admin role">
          <span>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      );
    },
  }));

  const columns = [
    { field: "username", headerName: "Username", flex: 0.8, minWidth: 140 },
    { field: "email", headerName: "E-mail", flex: 1, minWidth: 180 },
    ...roleColumns,
  ];

  return (
    <>
      <Box m="1.5rem 2.5rem">
        <Header title="Manage Roles" subtitle="Tick a box to grant a role, untick it to revoke" />
        <Box
          mt={"40px"}
          height={"75vh"}
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading || !data || !roles}
            getRowId={(row) => row._id}
            rows={data || []}
            columns={columns}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
      <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
        <DialogTitle>{pending?.grant ? "Grant role?" : "Revoke role?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending &&
              (pending.grant
                ? `Give ${pending.user.username} the ${pending.role} role?`
                : `Remove the ${pending.role} role from ${pending.user.username}?`)}{" "}
            Access changes immediately; their menu updates the next time they log in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button variant="contained" color={pending?.grant ? "secondary" : "error"} onClick={handleConfirm}>
            {pending?.grant ? "Grant" : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
            {notice.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </>
  );
};

export default BoardAdmin;
